// src/hr/HrShell.js
// Shared frame for the Time Off and HR Portal apps: a 192px side menu (brand on top, signed-in
// person at the bottom) and a content column whose top strip shows the breadcrumb on the left and
// keeps the top-right corner clear for the app's fixed Apps button + bell.
// Below 1100px the menu collapses to icons; below 700px it becomes an off-canvas menu opened from
// a top bar, and the content runs in a single column.
import React, { useEffect, useId, useRef, useState } from 'react';
import { Link, resolvePath, useLocation, useResolvedPath } from 'react-router-dom';
import { FiMenu } from 'react-icons/fi';
import { Avatar } from './kit';
import { plainId, renderIcon } from './kit/util';
import '../styles/HrPortal.css';

const trimSlash = (p) => (p.length > 1 ? p.replace(/\/+$/, '') : p);

// The current item is the one whose path is the longest match for the URL, so /time-off/whos-out
// marks "Who's Out" (not "My Time Off") and /hr-portal/people/123 keeps "People" current.
// An item with `end: true` only matches its exact path.
function currentItemKey(pathname, entries) {
  const here = trimSlash(pathname);
  let best = null;
  entries.forEach(({ key, path, end }) => {
    const p = trimSlash(path);
    const hit = here === p || (!end && (p === '/' || here.startsWith(`${p}/`)));
    if (hit && (!best || p.length > best.length)) best = { key, length: p.length };
  });
  return best ? best.key : null;
}

function Crumb({ brand, crumb }) {
  if (crumb != null && crumb !== false && typeof crumb !== 'string' && !Array.isArray(crumb)) return crumb;
  const parts = crumb == null || crumb === false ? [] : [].concat(crumb);
  return (
    <>
      <b>{brand}</b>
      {parts.map((part, i) => (
        <React.Fragment key={`${i}:${part}`}>
          <span className="hrp-crumb__sep" aria-hidden="true">·</span>
          <span>{part}</span>
        </React.Fragment>
      ))}
    </>
  );
}

export function HrShell({ brand, brandLetter, brandSub = 'Solum Internal', sections = [], person, crumb, children }) {
  const location = useLocation();
  const base = useResolvedPath('.').pathname;
  const navId = `hrp-nav-${plainId(useId())}`;
  const navRef = useRef(null);
  const toggleRef = useRef(null);
  const [menuOpen, setMenuOpen] = useState(false);

  const entries = [];
  sections.forEach((section, s) => (section.items || []).forEach((item, i) => {
    entries.push({ key: `${s}:${i}`, path: resolvePath(item.to, base).pathname, end: !!item.end });
  }));
  const current = currentItemKey(location.pathname, entries);

  // Close the small-screen menu whenever the page changes.
  useEffect(() => { setMenuOpen(false); }, [location.pathname]);

  useEffect(() => {
    if (!menuOpen) return undefined;
    const first = navRef.current && navRef.current.querySelector('a[href]');
    if (first) first.focus();
    const onKey = (e) => {
      if (e.key !== 'Escape') return;
      setMenuOpen(false);
      if (toggleRef.current) toggleRef.current.focus();
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [menuOpen]);

  const skipToContent = (e) => {
    e.preventDefault();
    const main = document.getElementById('hrp-main');
    if (main) main.focus();
  };

  const letter = brandLetter || (brand ? brand[0] : '');

  return (
    <div className="hrp-root hrp-shell">
      <a className="hrp-skip" href="#hrp-main" onClick={skipToContent}>Skip to content</a>

      <header className="hrp-mbar">
        <button
          ref={toggleRef}
          type="button"
          className="hrp-iconbtn hrp-mbar__menu"
          aria-label="Menu"
          aria-expanded={menuOpen}
          aria-controls={navId}
          onClick={() => setMenuOpen((open) => !open)}
        >
          <FiMenu aria-hidden="true" />
        </button>
        <span className="hrp-logo hrp-logo--sm" aria-hidden="true">{letter}</span>
        <span className="hrp-mbar__brand">{brand}</span>
      </header>

      <div className="hrp-navscrim" hidden={!menuOpen} aria-hidden="true" onClick={() => setMenuOpen(false)} />

      <nav id={navId} ref={navRef} className="hrp-snav" aria-label={`${brand} menu`} data-open={menuOpen ? 'true' : 'false'}>
        <div className="hrp-brand">
          <span className="hrp-logo" aria-hidden="true">{letter}</span>
          <div className="hrp-brand__txt">
            <b>{brand}</b>
            <span>{brandSub}</span>
          </div>
        </div>

        {sections.map((section, s) => {
          const titleId = `${navId}-s${s}`;
          return (
            <div className="hrp-sec" key={section.title || s}>
              {section.title && <div className="hrp-sec__title" id={titleId}>{section.title}</div>}
              <ul className="hrp-sec__list" aria-labelledby={section.title ? titleId : undefined}>
                {(section.items || []).map((item, i) => {
                  const isCurrent = current === `${s}:${i}`;
                  const hasBadge = item.badge != null && item.badge !== 0 && item.badge !== false && item.badge !== '';
                  return (
                    <li key={item.to}>
                      <Link
                        to={item.to}
                        className="hrp-it"
                        aria-current={isCurrent ? 'page' : undefined}
                        data-tip={item.label}
                      >
                        <span className="hrp-gi" aria-hidden="true">{renderIcon(item.icon)}</span>
                        <span className="hrp-it__label">{item.label}</span>
                        {hasBadge && <span className="hrp-it__badge">{item.badge}</span>}
                        {!hasBadge && item.count != null && <span className="hrp-it__count">{item.count}</span>}
                      </Link>
                    </li>
                  );
                })}
              </ul>
            </div>
          );
        })}

        <div className="hrp-snav__fill" aria-hidden="true" />

        {person && person.name && (
          <div className="hrp-me" title={person.name}>
            <Avatar name={person.name} id={person.id} size="md" />
            <div className="hrp-me__txt">
              <b>{person.name}</b>
              {person.sub && <span>{person.sub}</span>}
            </div>
          </div>
        )}
      </nav>

      <main id="hrp-main" className="hrp-main" tabIndex={-1}>
        <div className="hrp-main__inner">
          <div className="hrp-strip">
            <div className="hrp-crumb" data-testid="hrp-crumb"><Crumb brand={brand} crumb={crumb} /></div>
          </div>
          {children}
        </div>
      </main>
    </div>
  );
}

export default HrShell;
